import { createFileRoute, Link, Outlet, useMatches } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { FolderOpen, Plus } from "lucide-react";
import { listAdminCases, createCase, updateCase } from "@/lib/clinic.functions";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";

const listQ = () => queryOptions({ queryKey: ["admin", "cases"], queryFn: () => listAdminCases() });

export const Route = createFileRoute("/_authenticated/admin/cases")({
  loader: ({ context }) => context.queryClient.ensureQueryData(listQ()),
  errorComponent: ({ error }) => <p className="p-6 text-muted-foreground">{String(error)}</p>,
  notFoundComponent: RouteNotFound,
  component: CasesShell,
});

function CasesShell() {
  const matches = useMatches();
  const onDetail = matches.some((m) => m.routeId === "/_authenticated/admin/cases/$id");
  if (onDetail) return <Outlet />;
  return <CasesList />;
}

function CasesList() {
  const { data } = useSuspenseQuery(listQ());
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const filtered = data.filter((c: any) => {
    if (statusFilter !== "all" && c.status !== statusFilter) return false;
    if (!q) return true;
    const hay = `${c.child_alias} ${c.parent?.full_name ?? ""} ${(c.tags ?? []).join(" ")}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="kicker text-modern-orange">حالات العيادة</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold text-foreground">إدارة الحالات</h1>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="بحث بالاسم أو الوسم..."
            className="w-56 rounded-full border border-input bg-card px-4 py-2 text-sm outline-none focus:border-ring"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-full border border-input bg-card px-3 py-2 text-sm outline-none"
          >
            <option value="all">كل الحالات</option>
            <option value="active">نشطة</option>
            <option value="paused">موقوفة</option>
            <option value="closed">مغلقة</option>
          </select>
          <NewCaseButton />
        </div>
      </header>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-background/50 px-6 py-16 text-center">
          <FolderOpen className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <p className="text-muted-foreground">لا حالات بعد. أنشئ حالة جديدة أو حوّل حجزاً مؤكداً إلى حالة.</p>
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((c: any) => (
            <li key={c.id}>
              <Link
                to="/admin/cases/$id"
                params={{ id: c.id }}
                className="block rounded-2xl bg-card p-5 shadow-soft transition-all hover:shadow-lift"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-display text-base font-bold text-foreground">{c.child_alias}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {c.parent?.full_name ?? "—"} · {c.child_age_band ?? "—"}
                    </p>
                  </div>
                  <StatusBadge status={c.status} />
                </div>
                {(c.tags ?? []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {c.tags.map((t: string) => (
                      <span key={t} className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-foreground">{t}</span>
                    ))}
                  </div>
                )}
                {c.summary && <p className="mt-3 line-clamp-2 text-xs text-muted-foreground">{c.summary}</p>}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NewCaseButton() {
  const [open, setOpen] = useState(false);
  const [alias, setAlias] = useState("");
  const [age, setAge] = useState("");
  const [profileId, setProfileId] = useState("");
  const [busy, setBusy] = useState(false);
  const create = useServerFn(createCase);
  const qc = useQueryClient();

  async function submit() {
    if (!alias || !profileId) return toast.error("الاسم ومعرّف ولي الأمر مطلوبان");
    setBusy(true);
    try {
      await create({ data: { child_alias: alias, child_age_band: age || null, client_profile_id: profileId } });
      toast.success("تم إنشاء الحالة");
      qc.invalidateQueries({ queryKey: ["admin", "cases"] });
      setOpen(false);
      setAlias(""); setAge(""); setProfileId("");
    } catch (e: any) { toast.error(e?.message ?? "فشل"); } finally { setBusy(false); }
  }

  return (
    <>
      <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 font-display text-sm font-bold text-primary-foreground shadow-soft hover:bg-primary-hover">
        <Plus className="h-4 w-4" /> حالة جديدة
      </button>
      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-foreground/30 backdrop-blur-sm p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md rounded-2xl bg-card p-6 shadow-lift" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-4 font-display text-lg font-extrabold">حالة جديدة</h3>
            <div className="space-y-3 text-sm">
              <label className="block">
                <span className="mb-1 block text-muted-foreground">اسم/كنية الطفل</span>
                <input value={alias} onChange={(e) => setAlias(e.target.value)} className="w-full rounded-xl border border-input bg-background px-3 py-2 outline-none" />
              </label>
              <label className="block">
                <span className="mb-1 block text-muted-foreground">الفئة العمرية</span>
                <input value={age} onChange={(e) => setAge(e.target.value)} placeholder="مثلاً: 6-9 سنوات" className="w-full rounded-xl border border-input bg-background px-3 py-2 outline-none" />
              </label>
              <label className="block">
                <span className="mb-1 block text-muted-foreground">معرّف ملف ولي الأمر (UUID)</span>
                <input value={profileId} onChange={(e) => setProfileId(e.target.value)} className="w-full rounded-xl border border-input bg-background px-3 py-2 font-mono text-xs outline-none" />
                <span className="mt-1 block text-[10px] text-muted-foreground">استخدم زر «إنشاء حالة» من درج الحجز لملء الحقول تلقائياً.</span>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setOpen(false)} className="rounded-full border border-input px-4 py-2 text-sm">إلغاء</button>
                <button onClick={submit} disabled={busy} className="rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground disabled:opacity-50">
                  {busy ? "..." : "إنشاء"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    active: { label: "نشطة", cls: "bg-soft-green/30 text-foreground" },
    paused: { label: "موقوفة", cls: "bg-modern-orange/20 text-foreground" },
    closed: { label: "مغلقة", cls: "bg-border text-muted-foreground" },
  };
  const s = map[status] ?? { label: status, cls: "bg-border" };
  return <span className={`rounded-full px-3 py-1 font-display text-[10px] font-bold ${s.cls}`}>{s.label}</span>;
}

export { updateCase };